import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Form as AntdForm } from 'antd';
import { required } from '../../../../antd/form/FormValidation';
import PhdFormBuilder from '../PhdFormBuilder';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import {
  CONCLUSION_OPTION_KEYS,
  translateOptions,
} from '../phdFormOptions.jsx';
import { createSchoolYearChangeHandler } from '../utils/usePhdSchoolYearOptions';
import { isStudentActiveInSchoolYear } from '../utils/isStudentActiveInSchoolYear';
import { resolveStudentLabel } from '../usePhdReferenceData';
import PhdStudentEvaluationGradesSection from './PhdStudentEvaluationGradesSection.jsx';
import {
  getPhdDocumentDependencyState,
  PHD_DOCUMENT_DEPENDENCY_TYPES,
} from '../utils/phdDocumentDependencies.jsx';

const hasEvaluationAuthoringContent = (evaluation) => {
  if (!evaluation) {
    return false;
  }

  return [
    evaluation.conclusion,
    evaluation.credits_evaluation,
    evaluation.scientific_evaluation,
    evaluation.assignments_state,
    evaluation.modification_proposal,
    evaluation.additional_evaluation,
    evaluation.date_planned_dissertation_exam,
  ].some(
    (value) =>
      value !== null && value !== undefined && `${value}`.trim() !== '',
  );
};

const PhdStudentEvaluationsEditForm = ({
  studentDisplayNameMap,
  students,
  schoolYears,
  currentUserPersonId,
  isAdmin = false,
  isEdit = false,
  currentEvaluationId,
  schoolYearOptions,
  ensureSchoolYearSelection,
  isCreatingSchoolYear,
  isSubmitting,
  serviceMode = 'evaluation',
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const form = AntdForm.useFormInstance();
  const selectedSchoolYearId = AntdForm.useWatch('phd_school_year_id', form);
  const selectedStudentId = AntdForm.useWatch('phd_student_id', form);
  const handleSchoolYearChange = createSchoolYearChangeHandler(
    form,
    ensureSchoolYearSelection,
  );
  const isGradesMode = serviceMode === 'grades';
  const isEvaluationCreateMode = serviceMode === 'evaluation' && !isEdit;
  const studentFieldDisabled =
    (serviceMode === 'evaluation' && isEdit) || (isGradesMode && isEdit);
  const schoolYearFieldDisabled =
    (serviceMode === 'evaluation' && isEdit) || (isGradesMode && isEdit);

  const conclusionOptions = useMemo(
    () => translateOptions(CONCLUSION_OPTION_KEYS, t),
    [t],
  );

  const filteredStudentOptions = useMemo(() => {
    const eligibleStudents = (students || []).filter((student) => {
      if (
        !isStudentActiveInSchoolYear(student, schoolYears, selectedSchoolYearId)
      ) {
        return false;
      }

      const matchingEvaluations = (student.evaluations || []).filter(
        (evaluation) =>
          Number(evaluation.phd_school_year_id) ===
          Number(selectedSchoolYearId),
      );
      const matchingGrades = (student.evaluation_grades || []).filter(
        (grade) =>
          Number(grade.phd_school_year_id) === Number(selectedSchoolYearId),
      );
      const hasCreatableEvaluation = matchingGrades.some(
        (grade) => grade.grades_submitted_at,
      );

      if (isEvaluationCreateMode && !hasCreatableEvaluation) {
        return false;
      }

      const hasAnotherEvaluationInSchoolYear = matchingEvaluations.some(
        (evaluation) => Number(evaluation.id) !== Number(currentEvaluationId),
      );
      const hasAnotherGradesEntryInSchoolYear = matchingGrades.some(
        (grade) => Number(grade.id) !== Number(currentEvaluationId),
      );

      if (hasAnotherEvaluationInSchoolYear) {
        return false;
      }

      if (isGradesMode && hasAnotherGradesEntryInSchoolYear) {
        return false;
      }

      if (isAdmin || isGradesMode) {
        return true;
      }

      const assignedTheses = (student.theses || []).filter(
        (thesis) => Number(thesis.phd_student_id) === Number(student.id),
      );
      if (assignedTheses.length === 0) {
        return false;
      }

      if (!(student.individual_plans || []).length) {
        return false;
      }

      if (!currentUserPersonId) {
        return false;
      }

      return assignedTheses.some(
        (thesis) =>
          Number(thesis.phd_supervisor_id) === Number(currentUserPersonId),
      );
    });

    if (
      selectedStudentId &&
      !eligibleStudents.some((student) => student.id === selectedStudentId)
    ) {
      const selectedStudent = (students || []).find(
        (student) => student.id === selectedStudentId,
      );

      if (selectedStudent) {
        eligibleStudents.push(selectedStudent);
      }
    }

    return eligibleStudents.map((student) => ({
      value: student.id,
      label: resolveStudentLabel(student, studentDisplayNameMap),
    }));
  }, [
    currentEvaluationId,
    currentUserPersonId,
    isAdmin,
    isEvaluationCreateMode,
    isGradesMode,
    schoolYears,
    selectedSchoolYearId,
    selectedStudentId,
    studentDisplayNameMap,
    students,
  ]);

  useEffect(() => {
    if (
      !selectedStudentId ||
      isEdit ||
      filteredStudentOptions.some(
        (option) => option.value === selectedStudentId,
      )
    ) {
      return;
    }

    form.setFieldValue('phd_student_id', undefined);
  }, [filteredStudentOptions, form, isEdit, selectedStudentId]);

  const studentField = {
    name: 'phd_student_id',
    label: t('Študent'),
    type: 'select',
    options: filteredStudentOptions,
    rules: [required(t)],
    componentProps: {
      disabled: studentFieldDisabled,
    },
    colSpan: 12,
  };

  const schoolYearField = {
    name: 'phd_school_year_id',
    label: (
      <span>
        {t('Školský rok')}
        <PhdHelpHint
          label={t('Čo znamená hodnotený školský rok?')}
          title={t(
            'Rok určuje obdobie známok alebo ročného hodnotenia. Nejde o rok vzniku individuálneho plánu.',
          )}
        />
      </span>
    ),
    type: 'select',
    options: schoolYearOptions,
    componentProps: {
      onChange: handleSchoolYearChange,
      loading: isCreatingSchoolYear,
      disabled: schoolYearFieldDisabled,
    },
    rules: [required(t)],
    colSpan: 12,
  };

  const sections = useMemo(() => {
    if (isGradesMode) {
      return [
        {
          key: 'basic',
          title: t('Základné údaje'),
          fields: [studentField, schoolYearField],
        },
        {
          key: 'subjects_grades',
          title: t('Predmety individuálneho plánu a známky'),
          fields: [
            {
              name: 'subjects_grades_control',
              render: () => <PhdStudentEvaluationGradesSection />,
              colSpan: 24,
            },
          ],
        },
      ];
    }

    return [
      {
        key: 'basic',
        title: t('Základné údaje'),
        fields: [
          studentField,
          schoolYearField,
          {
            name: 'conclusion',
            label: t('Záver'),
            type: 'select',
            options: conclusionOptions,
            colSpan: 12,
          },
          {
            name: 'thesis_state',
            label: t('Stav práce'),
            type: 'text',
            componentProps: {
              disabled: true,
            },
            colSpan: 12,
          },
        ],
      },
      {
        key: 'evaluations',
        title: t('Hodnotenia'),
        fields: [
          {
            name: 'credits_evaluation',
            label: t('Kredity'),
            type: 'editor',
            colSpan: 24,
          },
          {
            name: 'scientific_evaluation',
            label: t('Vedecká činnosť'),
            type: 'editor',
            colSpan: 24,
          },
          {
            name: 'assignments_state',
            label: t('Úlohy'),
            type: 'editor',
            colSpan: 24,
          },
          {
            name: 'modification_proposal',
            label: t('Návrh úprav'),
            type: 'editor',
            colSpan: 24,
          },
          {
            name: 'additional_evaluation',
            label: t('Doplňujúce hodnotenie'),
            type: 'editor',
            colSpan: 24,
          },
        ],
      },
      {
        key: 'dates',
        title: t('Plánované termíny'),
        fields: [
          {
            name: 'date_planned_dissertation_application',
            label: t('Prihláška na dizertačnú prácu'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            colSpan: 8,
          },
          {
            name: 'date_planned_dissertation_submission',
            label: t('Odovzdanie dizertačnej práce'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            colSpan: 8,
          },
          {
            name: 'date_planned_dissertation_exam',
            label: t('Dizertačná skúška'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            colSpan: 8,
          },
        ],
      },
    ];
  }, [
    conclusionOptions,
    handleSchoolYearChange,
    isCreatingSchoolYear,
    isGradesMode,
    schoolYearField,
    studentField,
    t,
  ]);

  const selectedStudent = useMemo(
    () =>
      (students || []).find(
        (student) => Number(student.id) === Number(selectedStudentId),
      ),
    [selectedStudentId, students],
  );

  const hasCreatableEvaluationForSelection = useMemo(() => {
    if (!isEvaluationCreateMode || !selectedStudent || !selectedSchoolYearId) {
      return false;
    }

    return (selectedStudent.evaluation_grades || []).some(
      (grade) =>
        Number(grade.phd_school_year_id) === Number(selectedSchoolYearId) &&
        grade.grades_submitted_at,
    );
  }, [isEvaluationCreateMode, selectedSchoolYearId, selectedStudent]);

  const showMissingGradesForEvaluation =
    isEvaluationCreateMode &&
    Boolean(selectedStudentId) &&
    Boolean(selectedSchoolYearId) &&
    !hasCreatableEvaluationForSelection;

  const dependencyState = useMemo(
    () =>
      isGradesMode
        ? getPhdDocumentDependencyState({
            documentType:
              PHD_DOCUMENT_DEPENDENCY_TYPES.STUDENT_EVALUATION_GRADES,
            student: selectedStudent,
            schoolYearId: selectedSchoolYearId,
          })
        : { missingItems: [], message: null },
    [isGradesMode, selectedSchoolYearId, selectedStudent],
  );

  const isSubmitDisabled =
    (isGradesMode && dependencyState.missingItems.length > 0) ||
    showMissingGradesForEvaluation;

  return (
    <PhdFormBuilder
      sections={sections}
      submitKey="phd-student-evaluations"
      isSubmitting={isSubmitting}
      isSubmitDisabled={isSubmitDisabled}
      hideSubmit={isReadOnly}
      beforeSubmitContent={
        isGradesMode ? (
          <>
            {dependencyState.message ? (
              <Alert
                type="warning"
                showIcon
                message={t('Chýbajúce podklady')}
                description={dependencyState.message}
                style={{ marginTop: 16 }}
              />
            ) : null}
          </>
        ) : showMissingGradesForEvaluation ? (
          <Alert
            type="warning"
            showIcon
            message={t('Chýbajúce známky')}
            description={t(
              'Ročné hodnotenie je možné vytvoriť až po zadaní známok v zvolenom školskom roku.',
            )}
            style={{ marginTop: 16 }}
          />
        ) : null
      }
    />
  );
};

export default PhdStudentEvaluationsEditForm;
